// frontend/src/pages/configuracoes/auditoria/Auditoria.js

import React, { useEffect, useMemo, useState } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { formatDateWithHour } from "../../../utils/date";
import HistoricoRegistroModal from "./HistoricoRegistroModal";
import DetalhesAuditoriaModal from "./DetalhesAuditoriaModal";

const OPERACOES = [
    { valor: "create", label: "Criação" },
    { valor: "update", label: "Atualização" },
    { valor: "delete", label: "Exclusão" },
    { valor: "upsert", label: "Upsert" },
    { valor: "deleteMany", label: "Exclusão em massa" },
];

const operacoesLabel = OPERACOES.reduce((acc, o) => {
    acc[o.valor] = o.label;
    return acc;
}, {});

const operacaoBadgeClass = (operacao) => {
    switch (operacao) {
        case "create":
            return "bg-emerald-100 text-emerald-800";
        case "update":
            return "bg-amber-100 text-amber-800";
        case "delete":
        case "deleteMany":
            return "bg-red-100 text-red-800";
        case "upsert":
            return "bg-indigo-100 text-indigo-800";
        default:
            return "bg-stone-100 text-stone-800";
    }
};

const Auditoria = () => {
    const { auth } = useAuth();
    const isAdmin = auth?.user?.role === "ADMIN";

    const [tabelas, setTabelas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const [filters, setFilters] = useState({
        tabela: "",
        registroId: "",
        usuarioId: "",
        operacao: "",
        dataInicio: "",
        dataFim: "",
    });

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        total: 0,
        totalPages: 0,
    });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const [historicoOpen, setHistoricoOpen] = useState(false);
    const [historicoTarget, setHistoricoTarget] = useState({ tabela: "", registroId: null });

    const [detalheOpen, setDetalheOpen] = useState(false);
    const [detalheItem, setDetalheItem] = useState(null);

    useEffect(() => {
        if (!isAdmin) return;
        const carregarMetadados = async () => {
            try {
                const [tabelasResp, usersResp] = await Promise.all([
                    api.get("/auditoria/tabelas"),
                    api.get("/users"),
                ]);
                setTabelas(tabelasResp.data || []);
                setUsuarios(usersResp.data || []);
            } catch (err) {
                console.error("Erro ao carregar metadados de auditoria:", err);
            }
        };
        carregarMetadados();
    }, [isAdmin]);

    const fetchAuditorias = async (overrides = {}) => {
        setLoading(true);
        setErrorMsg("");
        try {
            const params = {
                page: pagination.page,
                pageSize: pagination.pageSize,
                ...filters,
                ...overrides,
            };
            Object.keys(params).forEach((k) => {
                if (params[k] === "" || params[k] == null) delete params[k];
            });

            const response = await api.get("/auditoria", { params });
            setItems(response.data.items || []);
            setPagination((prev) => ({
                ...prev,
                page: response.data.page,
                pageSize: response.data.pageSize,
                total: response.data.total,
                totalPages: response.data.totalPages,
            }));
        } catch (err) {
            console.error("Erro ao carregar auditorias:", err);
            setErrorMsg(
                err.response?.data?.msg || "Erro ao carregar histórico de auditoria."
            );
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) return;
        fetchAuditorias({ page: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleAplicarFiltros = (e) => {
        e?.preventDefault();
        setPagination((prev) => ({ ...prev, page: 1 }));
        fetchAuditorias({ page: 1 });
    };

    const handleLimparFiltros = () => {
        const resetFilters = {
            tabela: "",
            registroId: "",
            usuarioId: "",
            operacao: "",
            dataInicio: "",
            dataFim: "",
        };
        setFilters(resetFilters);
        setPagination((prev) => ({ ...prev, page: 1 }));
        fetchAuditorias({ ...resetFilters, page: 1 });
    };

    const handleVerHistorico = (item) => {
        setHistoricoTarget({ tabela: item.tabela, registroId: item.registroId });
        setHistoricoOpen(true);
    };

    const handleVerDetalhes = (item) => {
        setDetalheItem(item);
        setDetalheOpen(true);
    };

    const handleAbrirHistoricoPorFiltro = () => {
        if (!filters.tabela || !filters.registroId) {
            setErrorMsg("Selecione uma tabela e informe o ID do registro para ver o histórico completo.");
            return;
        }
        setHistoricoTarget({
            tabela: filters.tabela,
            registroId: parseInt(filters.registroId, 10),
        });
        setHistoricoOpen(true);
    };

    const irParaPagina = (pagina) => {
        if (pagina < 1 || pagina > pagination.totalPages) return;
        setPagination((prev) => ({ ...prev, page: pagina }));
        fetchAuditorias({ page: pagina });
    };

    const exibicaoPagina = useMemo(() => {
        if (!pagination.total) return "0 registros";
        const inicio = (pagination.page - 1) * pagination.pageSize + 1;
        const fim = Math.min(pagination.page * pagination.pageSize, pagination.total);
        return `${inicio}–${fim} de ${pagination.total}`;
    }, [pagination]);

    if (!isAdmin) {
        return (
            <Template>
                <div className="w-full px-4 py-6">
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-sm">
                        Acesso restrito a administradores.
                    </div>
                </div>
            </Template>
        );
    }

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-6">
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Auditoria de Dados</h1>
                    <p className="text-sm text-stone-600 mt-1">
                        Histórico de criações, atualizações e exclusões de registros.
                    </p>
                </div>

                <form
                    onSubmit={handleAplicarFiltros}
                    className="bg-white p-6 rounded-md border border-slate-200 flex flex-col gap-4"
                >
                    <div className="text-stone-600 text-base font-medium">Filtros</div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Tabela</label>
                            <select
                                name="tabela"
                                value={filters.tabela}
                                onChange={handleFilterChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                            >
                                <option value="">Todas</option>
                                {tabelas.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">ID do Registro</label>
                            <input
                                type="number"
                                name="registroId"
                                value={filters.registroId}
                                onChange={handleFilterChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Ex.: 123"
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Operação</label>
                            <select
                                name="operacao"
                                value={filters.operacao}
                                onChange={handleFilterChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                            >
                                <option value="">Todas</option>
                                {OPERACOES.map((op) => (
                                    <option key={op.valor} value={op.valor}>{op.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Usuário</label>
                            <select
                                name="usuarioId"
                                value={filters.usuarioId}
                                onChange={handleFilterChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                            >
                                <option value="">Todos</option>
                                {usuarios.map((u) => (
                                    <option key={u.id} value={u.id}>{u.nome}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">De</label>
                            <input
                                type="datetime-local"
                                name="dataInicio"
                                value={filters.dataInicio}
                                onChange={handleFilterChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Até</label>
                            <input
                                type="datetime-local"
                                name="dataFim"
                                value={filters.dataFim}
                                onChange={handleFilterChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                        >
                            Aplicar Filtros
                        </button>
                        <button
                            type="button"
                            onClick={handleLimparFiltros}
                            className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                        >
                            Limpar
                        </button>
                        <button
                            type="button"
                            onClick={handleAbrirHistoricoPorFiltro}
                            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md disabled:opacity-50"
                            disabled={!filters.tabela || !filters.registroId}
                            title="Ver linha do tempo completa do registro"
                        >
                            Ver Histórico do Registro
                        </button>
                    </div>
                </form>

                {errorMsg && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
                        {errorMsg}
                    </div>
                )}

                <div className="bg-white p-6 rounded-md border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="text-stone-600 text-base font-medium">Eventos</div>
                        <div className="text-sm text-stone-500">{exibicaoPagina}</div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">Data</th>
                                    <th className="px-4 py-2 border border-slate-200">Tabela</th>
                                    <th className="px-4 py-2 border border-slate-200">Registro</th>
                                    <th className="px-4 py-2 border border-slate-200">Operação</th>
                                    <th className="px-4 py-2 border border-slate-200">Usuário</th>
                                    <th className="px-4 py-2 border border-slate-200">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-slate-700">
                                            Carregando...
                                        </td>
                                    </tr>
                                ) : items.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-slate-700">
                                            Nenhum evento de auditoria encontrado.
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((item) => (
                                        <tr key={item.id} className="border-b">
                                            <td className="px-4 py-2 border border-slate-200 whitespace-nowrap">
                                                {formatDateWithHour(item.data)}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">{item.tabela}</td>
                                            <td className="px-4 py-2 border border-slate-200">#{item.registroId}</td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${operacaoBadgeClass(item.operacao)}`}>
                                                    {operacoesLabel[item.operacao] || item.operacao}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {item.usuario ? (
                                                    <div className="flex flex-col">
                                                        <span>{item.usuario.nome}</span>
                                                        <span className="text-xs text-stone-500">{item.usuario.email}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-stone-400 text-sm italic">Sistema</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    onClick={() => handleVerDetalhes(item)}
                                                    className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                                                >
                                                    Detalhes
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleVerHistorico(item)}
                                                    className="px-2 py-1 bg-indigo-600 text-white rounded-md text-sm"
                                                    title="Ver linha do tempo deste registro"
                                                >
                                                    Histórico
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <button
                                type="button"
                                onClick={() => irParaPagina(pagination.page - 1)}
                                className="px-3 py-1 bg-stone-200 text-stone-700 rounded-md text-sm disabled:opacity-50"
                                disabled={pagination.page <= 1}
                            >
                                Anterior
                            </button>
                            <div className="text-sm text-stone-600">
                                Página {pagination.page} de {pagination.totalPages}
                            </div>
                            <button
                                type="button"
                                onClick={() => irParaPagina(pagination.page + 1)}
                                className="px-3 py-1 bg-stone-200 text-stone-700 rounded-md text-sm disabled:opacity-50"
                                disabled={pagination.page >= pagination.totalPages}
                            >
                                Próxima
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {historicoOpen && (
                <HistoricoRegistroModal
                    tabela={historicoTarget.tabela}
                    registroId={historicoTarget.registroId}
                    onClose={() => setHistoricoOpen(false)}
                />
            )}

            {detalheOpen && detalheItem && (
                <DetalhesAuditoriaModal
                    auditoriaId={detalheItem.id}
                    onClose={() => {
                        setDetalheOpen(false);
                        setDetalheItem(null);
                    }}
                />
            )}
        </Template>
    );
};

export default Auditoria;
